import {
  collection,
  DocumentData,
  getDocs,
  getFirestore,
  limit,
  orderBy,
  query,
  QuerySnapshot,
  startAfter,
  where,
} from "firebase/firestore";
import { useInfiniteQuery } from "react-query";
import {
  convertLessonAlternativeLinkSnapToILessonAlternativeLink,
  ILessonAlternativeLink,
} from "../../types/firestoreMappings";
import { collectionPaths } from "../collectionPaths";
import { settledLogger } from "../settledLogger";
import { useGetLessonAlternativeLinkFetch } from "./useGetLessonAlternativeLinkQuery";

export interface ListLessonAlternativeLinksQueryParams {
  courseId?: string;
  lessonId?: string;
  sortByUpvotes?: boolean;
  topItemId?: string | null;
  numItemsPerPage?: number;
}

const defaultNumItemsPerPage = 3;

export const listLessonAlternativeLinksQueryKey = (
  params: ListLessonAlternativeLinksQueryParams
) => {
  return ["listLessonAlternativeLinksQueryKey", params];
};

export const useListLessonAlternativeLinksQuery = (
  params: ListLessonAlternativeLinksQueryParams,
  enabled: boolean = true
) => {
  const fetchAlternativeLink = useGetLessonAlternativeLinkFetch();
  const listLessonAlternativeLinks = async (vars: {
    pageParam?: {
      lastPage: {
        realData: ILessonAlternativeLink[];
        snaps: QuerySnapshot<DocumentData>;
      };
    };
  }) => {
    const { pageParam } = vars;
    const { courseId, lessonId, sortByUpvotes, topItemId, numItemsPerPage } =
      params;
    const numItemsPerPageToUse = numItemsPerPage || defaultNumItemsPerPage;
    let isLastPage = false;
    const db = getFirestore();
    const lessonAlternativeLinksCollectionRef = collection(
      db,
      collectionPaths.lessonAlternativeLinks
    );
    const queryConstraints = [];
    if (sortByUpvotes) {
      queryConstraints.push(orderBy("upvotes", "desc"));
    }
    // Always sort by createdAt by default
    queryConstraints.push(orderBy("createdAt", "desc"));
    if (courseId) {
      queryConstraints.push(where("courseId", "==", courseId));
    }
    if (lessonId) {
      queryConstraints.push(where("lessonId", "==", lessonId));
    }
    // Do pagination stuff
    if (pageParam) {
      const lastSnap =
        pageParam.lastPage.snaps.docs[pageParam.lastPage.snaps.docs.length - 1];
      if (lastSnap) {
        queryConstraints.push(startAfter(lastSnap));
      }
    }
    const q = query(
      lessonAlternativeLinksCollectionRef,
      ...queryConstraints,
      limit(numItemsPerPageToUse)
    );
    const qSnapshot = await getDocs(q);
    let lessonAlternativeLinks = qSnapshot.docs.map((snapshot) =>
      convertLessonAlternativeLinkSnapToILessonAlternativeLink(
        snapshot.id,
        snapshot.data()
      )
    );

    if (lessonAlternativeLinks.length < numItemsPerPageToUse) {
      isLastPage = true;
    }

    if (topItemId) {
      // Filter out specified top item to avoid duplicates
      lessonAlternativeLinks = lessonAlternativeLinks.filter(
        (alternativeLink) => alternativeLink.id !== topItemId
      );
      const isFirstPage = !pageParam;
      if (isFirstPage) {
        // Only add specified top item if first page load, otherwise it duplicates
        const topAlternativeLink = await fetchAlternativeLink({
          lessonAlternativeLinkId: topItemId,
        });
        lessonAlternativeLinks = [topAlternativeLink].concat(
          lessonAlternativeLinks
        );
      }
    }

    return {
      realData: lessonAlternativeLinks,
      snaps: qSnapshot,
      isLastPage,
    };
  };
  const paginatedResult = useInfiniteQuery(
    listLessonAlternativeLinksQueryKey(params),
    listLessonAlternativeLinks,
    {
      getNextPageParam: (lastPage, allPages) => {
        return {
          lastPage,
          allPages,
        };
      },
      onSettled: settledLogger("useListLessonAlternativeLinksQuery"),
      enabled,
    }
  );

  let realDataAllPages: ILessonAlternativeLink[] = [];
  let isNoMoreToFetch = true;
  if (paginatedResult.data) {
    paginatedResult.data.pages.forEach((page) => {
      realDataAllPages = realDataAllPages.concat(page.realData);
    });
    isNoMoreToFetch =
      paginatedResult.data.pages[paginatedResult.data.pages.length - 1]
        .isLastPage;
  }

  return {
    ...paginatedResult,
    data: realDataAllPages,
    isNoMoreToFetch,
  };
};
